<template>
	<div class="blog-post-page">
		<HeadNav
			selectedLanguage="en"
			page="guides" />
		<BreadcrumbNav :items="breadcrumbItems" />

		<main class="article-main">
			<article class="post-article">
				<header class="post-hero">
					<p class="eyebrow">
						GB WhatsApp guide
					</p>
					<h1>{{ pageTitle }}</h1>
					<img
						class="post-image"
						:src="image"
						:alt="imageAlt || pageTitle">
				</header>

				<div class="article-body">
					<slot />
				</div>

				<nav
					v-if="previousPost || nextPost"
					class="post-nav"
					aria-label="Article navigation">
					<router-link
						v-if="previousPost"
						class="post-nav-link post-nav-prev"
						:to="previousPost.url">
						<span>Previous Post</span>
						<strong>{{ previousPost.title }}</strong>
					</router-link>
					<router-link
						v-if="nextPost"
						class="post-nav-link post-nav-next"
						:to="nextPost.url">
						<span>Next Post</span>
						<strong>{{ nextPost.title }}</strong>
					</router-link>
				</nav>
			</article>
		</main>
	</div>
</template>

<script>
import HeadNav from '@/components/HeadNav.vue'
import BreadcrumbNav from '@/components/BreadcrumbNav.vue'
import doc from '@/documents/blogs.js'
import '@/css/base.scss'

export default {
	name: 'BlogPostLayout',
	components: {
		HeadNav,
		BreadcrumbNav,
	},
	props: {
		pageTitle: {
			type: String,
			required: true,
		},
		metaDescription: {
			type: String,
			required: true,
		},
		image: {
			type: String,
			required: true,
		},
		imageAlt: {
			type: String,
			default: '',
		},
	},
	computed: {
		posts() {
			return doc.bloglist.en
		},
		currentIndex() {
			const normalizePath = (path) => path.replace(/\/?$/, '/')
			const currentPath = normalizePath(this.$route.path)

			return this.posts.findIndex((post) => {
				return post.name === this.$route.name || normalizePath(post.url) === currentPath
			})
		},
		previousPost() {
			if (this.currentIndex <= 0) {
				return null
			}
			return this.posts[this.currentIndex - 1]
		},
		nextPost() {
			if (this.currentIndex < 0 || this.currentIndex >= this.posts.length - 1) {
				return null
			}
			return this.posts[this.currentIndex + 1]
		},
		breadcrumbItems() {
			return [{ label: 'Guides', to: { name: 'en-guides' } }, { label: this.pageTitle }]
		},
	},
}
</script>

<style lang="scss">
.blog-post-page {
	background: #ffffff;
	color: #111111;
	font-family: 'Lora', Georgia, serif;
	font-size: 17px;
	line-height: 1.68;
	-webkit-font-smoothing: antialiased;
}

.blog-post-page .article-main {
	padding: 34px 0 72px;
}

.blog-post-page .post-article {
	width: min(920px, calc(100% - 32px));
	margin: 0 auto;
}

.blog-post-page .post-hero {
	padding: 34px 0 26px;
	border-bottom: 1px solid #dce9e2;
}

.blog-post-page .eyebrow {
	margin: 0 0 12px;
	color: #0f8f43;
	font-family: 'Oswald', Arial, sans-serif;
	font-size: 13px;
	font-weight: 400;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.blog-post-page h1,
.blog-post-page h2,
.blog-post-page h3 {
	margin: 0;
	color: #000000;
	font-family: 'Oswald', Arial, sans-serif;
	font-weight: 400;
	letter-spacing: 0;
}

.blog-post-page h1 {
	max-width: 820px;
	font-size: clamp(34px, 4vw, 48px);
	line-height: 1.18;
}

.blog-post-page .post-image {
	margin-top: 24px;
	width: 100%;
	max-height: none;
	display: block;
	border: 1px solid #d7e8de;
	border-radius: 8px;
	object-fit: contain;
	object-position: center;
	background: #f6faf8;
}

.blog-post-page .article-body {
	padding-top: 34px;
}

.blog-post-page .article-body h2 {
	margin: 38px 0 14px;
	font-size: 28px;
	line-height: 1.25;
}

.blog-post-page .article-body h3 {
	margin: 26px 0 10px;
	font-size: 21px;
	line-height: 1.3;
}

.blog-post-page .article-body p,
.blog-post-page .article-body li {
	color: #222222;
	line-height: 1.68;
}

.blog-post-page .article-body p {
	margin: 0 0 16px;
}

.blog-post-page .article-body a {
	color: #0f8f43;
	text-decoration: underline;
	text-underline-offset: 2px;
}

.blog-post-page .article-body ul,
.blog-post-page .article-body ol {
	margin: 0 0 18px;
	padding-left: 24px;
}

.blog-post-page .article-body li + li {
	margin-top: 7px;
}

.blog-post-page .table-wrap {
	width: 100%;
	margin: 22px 0 24px;
	overflow-x: auto;
	border: 1px solid #d7e8de;
	border-radius: 8px;
	background: #ffffff;
	box-shadow: 0 10px 24px rgba(16, 37, 28, 0.05);
}

.blog-post-page table {
	width: 100%;
	min-width: 640px;
	border-collapse: collapse;
	font-family: Arial, sans-serif;
	font-size: 16px;
}

.blog-post-page th,
.blog-post-page td {
	padding: 14px 16px;
	border-right: 1px solid #e5ece8;
	border-bottom: 1px solid #e5ece8;
	text-align: left;
	vertical-align: top;
}

.blog-post-page th:last-child,
.blog-post-page td:last-child {
	border-right: 0;
}

.blog-post-page tbody tr:last-child td {
	border-bottom: 0;
}

.blog-post-page th {
	background: #10251c;
	color: #ffffff;
	font-weight: 700;
}

.blog-post-page tbody tr:nth-child(even) td {
	background: #f7fcf9;
}

.blog-post-page .post-nav {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
	margin-top: 44px;
	padding-top: 26px;
	border-top: 1px solid #dce9e2;
}

.blog-post-page .post-nav-link {
	min-height: 118px;
	padding: 18px 20px;
	border: 1px solid #d7e8de;
	border-radius: 8px;
	background: #f7fcf9;
	color: #10251c;
	text-decoration: none;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease,
		transform 0.2s ease;
}

.blog-post-page .post-nav-link:hover {
	border-color: rgba(15, 143, 67, 0.45);
	box-shadow: 0 12px 26px rgba(16, 37, 28, 0.07);
	transform: translateY(-2px);
}

.blog-post-page .post-nav-link span,
.blog-post-page .post-nav-link strong {
	display: block;
}

.blog-post-page .post-nav-link span {
	margin-bottom: 8px;
	color: #0f8f43;
	font-family: 'Oswald', Arial, sans-serif;
	font-size: 13px;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.blog-post-page .post-nav-link strong {
	color: #10251c;
	font-family: 'Oswald', Arial, sans-serif;
	font-size: 21px;
	font-weight: 400;
	line-height: 1.3;
}

.blog-post-page .post-nav-next {
	text-align: right;
}

.blog-post-page .post-nav-next:first-child {
	grid-column: 2;
}

@media (max-width: 640px) {
	.blog-post-page {
		font-size: 16px;
	}

	.blog-post-page .article-main {
		padding-bottom: 54px;
	}

	.blog-post-page .post-hero {
		padding-top: 24px;
	}

	.blog-post-page h1 {
		font-size: 30px;
	}

	.blog-post-page .article-body h2 {
		font-size: 24px;
	}

	.blog-post-page .post-nav {
		grid-template-columns: 1fr;
	}

	.blog-post-page .post-nav-next,
	.blog-post-page .post-nav-next:first-child {
		grid-column: auto;
		text-align: left;
	}
}
</style>
